// client/src/coms/components/comslayout_parts/useServersState.js
import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchServersFromApi } from './serverApi';

export default function useServersState() {
  const [servers, setServers] = useState([]);
  const [serversLoaded, setServersLoaded] = useState(false);
  const [apiAvailable, setApiAvailable] = useState(true);
  const [authState, setAuthState] = useState({ status: 'ok', message: '' });
  const [lastApiError, setLastApiError] = useState('');
  const refreshBusyRef = useRef(false);

  useEffect(() => {
    let alive = true;
    setServersLoaded(false);

    (async () => {
      try {
        const list = await fetchServersFromApi();
        if (!alive) return;

        setAuthState({ status: 'ok', message: '' });
        setApiAvailable(true);
        setLastApiError('');

        if (Array.isArray(list) && list.length) setServers(list);
        else setServers([]);

        setServersLoaded(true);
      } catch (e) {
        if (!alive) return;

        const status = e && typeof e.status === 'number' ? e.status : 0;
        const detail = String((e && e.message) || e || '');

        if (status === 401) {
          setAuthState({ status: 'auth', message: 'Not authenticated' });
          setApiAvailable(true);
          setLastApiError('');
          setServers([]);
          setServersLoaded(true);
          return;
        }

        if (status === 403) {
          setAuthState({ status: 'forbidden', message: 'Forbidden' });
          setApiAvailable(true);
          setLastApiError('');
          setServers([]);
          setServersLoaded(true);
          return;
        }

        setAuthState({ status: 'error', message: 'Comms API unavailable' });
        setApiAvailable(false);
        setLastApiError(detail);
        setServers([]);
        setServersLoaded(true);
      }
    })();

    return () => {
      alive = false;
    };
  }, []);

  const refreshServers = useCallback(
    async (force = false) => {
      if (!force && !apiAvailable) return;
      if (refreshBusyRef.current) return;
      refreshBusyRef.current = true;

      try {
        setLastApiError('');
        const list = await fetchServersFromApi();
        if (Array.isArray(list)) {
          setServers(list);
          setApiAvailable(true);
          setServersLoaded(true);
        }
      } catch (e) {
        const detail = String((e && e.message) || e || '');
        setApiAvailable(false);
        setLastApiError(detail);
        setServersLoaded(true);
      } finally {
        refreshBusyRef.current = false;
      }
    },
    [apiAvailable]
  );

  return {
    servers,
    setServers,
    serversLoaded,
    setServersLoaded,
    apiAvailable,
    setApiAvailable,
    authState,
    setAuthState,
    lastApiError,
    setLastApiError,
    refreshServers,
  };
}